import { useApolloClient } from "@apollo/client";
import { get, isEmpty, isNil } from "lodash";
import { useEffect, useState } from "react";
import graphql from "utils/api/graphql";
import { PDF_CONTENT_TYPE } from "../../constants";
import { bytesOf } from "./pdf-reader";
import { readMarkers } from "./zone-markers";

const CACHE_SIZE = 4;
const UNREADABLE = "UNREADABLE_SOURCE";
const NOTHING_READ = { markers: [], headings: [], pages: [] };

const sources = new Map();

const remember = (key, promise) => {
  sources.delete(key);
  sources.set(key, promise);
  const stale = [...sources.keys()].slice(
    0,
    Math.max(0, sources.size - CACHE_SIZE),
  );
  for (const old of stale) sources.delete(old);
  return promise;
};

const fetchSource = async ({ client, id }) => {
  const result = await client.query({
    query: graphql.queries.DOCUMENT_TEMPLATE_SOURCE,
    variables: { where: { id } },
    fetchPolicy: "no-cache",
  });
  const source = get(result, "data.documentTemplateSource");
  if (isEmpty(get(source, "base64"))) throw new Error(UNREADABLE);
  const bytes = bytesOf(source.base64);
  const { markers, headings, pages } = await readMarkers(bytes).catch(
    () => NOTHING_READ,
  );
  return {
    bytes,
    markers,
    headings,
    pages,
    name: source.name ?? null,
    contentType: source.contentType || PDF_CONTENT_TYPE,
    pending: source.pending === true,
  };
};

const cachedSource = ({ client, id, key, pending }) =>
  sources.get(key) ??
  remember(
    key,
    fetchSource({ client, id }).then(
      (source) => {
        if (source.pending !== pending) sources.delete(key);
        return source;
      },
      (error) => {
        sources.delete(key);
        throw error;
      },
    ),
  );

const useZoneSource = ({ template: { id }, version, pending }) => {
  const client = useApolloClient();
  const key = isNil(version) ? null : `${id}:${version}`;
  const [state, setState] = useState({ key: null, source: null });

  useEffect(() => {
    if (isNil(key)) return undefined;
    let alive = true;
    cachedSource({ client, id, key, pending })
      .then((source) => {
        if (alive) setState({ key, source });
      })
      .catch(() => {
        if (alive) setState({ key, source: null });
      });
    return () => {
      alive = false;
    };
  }, [client, id, key, pending]);

  const current = !isNil(key) && state.key === key;
  return {
    source: current ? state.source : null,
    loading: !isNil(key) && !current,
    failed: current && isNil(state.source),
  };
};

export default useZoneSource;
